import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { useObserver } from "mobx-react-lite";
import { Link } from "react-router-dom";
import {
    EuiButton,
    EuiFieldSearch,
    EuiIcon,
    EuiSuperSelect,
    EuiToolTip
} from "@elastic/eui";
import dayjs from "dayjs";
import serverStore from "../../../../stores/server.store";
import uptimeStore from "../../../../stores/uptime.store";
import mongoDbStore from "../../../../stores/mongodb.store";
import userStore from "../../../../stores/user.store";
import TagsList from "../../../components/tags/TagsList";
import TagsMIniColors from "../../../components/tags/TagsMIniColors";
import FillBar from "../../../components/metrics/FillBar";
import utilsService from "../../../../servises/utils.service";
import { serverFilterTypes } from "../../../../constants/data.types";

const GROUPS = {
    server: {
        title: "Servers",
        singular: "server agent",
        icon: "createMultiMetricJob",
        route: "/servers",
        accent: "#3b82f6"
    },
    uptime: {
        title: "Uptime",
        singular: "uptime check",
        icon: "clock",
        route: "/uptime",
        accent: "#6366f1"
    },
    mongo: {
        title: "MongoDB",
        singular: "MongoDB monitor",
        icon: "database",
        route: "/mongo",
        accent: "#10b981"
    }
};

const SORT_OPTIONS = [
    serverFilterTypes.createdAt,
    serverFilterTypes.alarms,
    serverFilterTypes.name,
    serverFilterTypes.tags
];

const DEFAULT_ORDER = ["server", "uptime", "mongo"];

const statusMeta = {
    normal: {
        label: "Healthy",
        className: "healthy"
    },
    "no-data": {
        label: "Waiting",
        className: "waiting"
    },
    "old-data": {
        label: "Stale",
        className: "stale"
    },
    disabled: {
        label: "Paused",
        className: "paused"
    }
};

const PageStyled = styled.section`
  display: grid;
  gap: 16px;
`;

const PageHeaderStyled = styled.header`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 16px;
  padding: 24px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(20, 20, 25, 0.7) 0%, rgba(10, 10, 12, 0.9) 100%);
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.25);

  h1 {
    margin: 0;
    font-size: 26px;
    font-weight: 800;
    color: var(--text-primary);
    letter-spacing: -0.5px;
  }

  p {
    margin: 6px 0 0;
    max-width: 720px;
    color: var(--text-secondary);
    line-height: 1.5;
    font-size: 14px;
  }

  .header-actions {
    display: flex;
    justify-content: flex-end;
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    padding: 20px;

    .header-actions {
      justify-content: flex-start;
    }
  }
`;

const SummaryGridStyled = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCardStyled = styled.div`
  min-height: 96px;
  padding: 16px 20px;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(20, 20, 25, 0.7) 0%, rgba(10, 10, 12, 0.9) 100%);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  .summary-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    color: var(--text-secondary);
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    
    .card-icon {
      color: var(--text-muted);
      transition: color 0.25s ease;
    }
  }

  .summary-value {
    margin-top: 8px;
    font-size: 30px;
    font-weight: 700;
    color: var(--text-primary);
    line-height: 1.1;
  }

  .summary-caption {
    margin-top: 6px;
    color: var(--text-muted);
    font-size: 12px;
    font-weight: 500;
  }
  
  &:hover {
    transform: translateY(-2px);
    border-color: rgba(255, 255, 255, 0.12);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.45);
    
    .card-icon {
      color: var(--text-primary);
    }
  }
  
  &.glow-danger {
    border-color: rgba(244, 63, 94, 0.4);
    background: linear-gradient(135deg, rgba(244, 63, 94, 0.04) 0%, rgba(10, 10, 12, 0.9) 100%);
    box-shadow: 0 4px 24px -2px rgba(244, 63, 94, 0.15);
    
    .summary-value, .card-icon {
      color: var(--color-danger);
    }
    
    &:hover {
      border-color: rgba(244, 63, 94, 0.6);
      box-shadow: 0 8px 32px rgba(244, 63, 94, 0.25);
    }
  }

  &.glow-warning {
    border-color: rgba(245, 158, 11, 0.4);
    background: linear-gradient(135deg, rgba(245, 158, 11, 0.03) 0%, rgba(10, 10, 12, 0.9) 100%);
    box-shadow: 0 4px 24px -2px rgba(245, 158, 11, 0.12);
    
    .summary-value, .card-icon {
      color: var(--color-warning);
    }
    
    &:hover {
      border-color: rgba(245, 158, 11, 0.6);
      box-shadow: 0 8px 32px rgba(245, 158, 11, 0.2);
    }
  }

  &.glow-primary {
    &:hover {
      border-color: rgba(99, 102, 241, 0.4);
      box-shadow: 0 8px 32px rgba(99, 102, 241, 0.15);
      .card-icon { color: var(--color-primary); }
    }
  }

  &.glow-info {
    &:hover {
      border-color: rgba(14, 165, 233, 0.4);
      box-shadow: 0 8px 32px rgba(14, 165, 233, 0.15);
      .card-icon { color: var(--color-info); }
    }
  }
`;

const ToolbarStyled = styled.div`
  display: grid;
  grid-template-columns: minmax(220px, 1.2fr) auto auto auto;
  gap: 14px;
  align-items: center;
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(20, 20, 25, 0.7) 0%, rgba(10, 10, 12, 0.9) 100%);
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37);
  backdrop-filter: blur(12px);

  .tag-filter {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .toolbar-label {
    color: var(--text-muted);
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .type-filter,
  .view-toggle {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  @media (max-width: 1150px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const SortControlStyled = styled.div`
  display: grid;
  grid-template-columns: 185px auto;
  gap: 8px;
  align-items: center;

  @media (max-width: 720px) {
    grid-template-columns: 1fr auto;
  }
`;

const IconButtonStyled = styled.button`
  width: 36px;
  height: 36px;
  display: inline-grid;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.02);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover:not(:disabled) {
    border-color: var(--color-primary);
    color: var(--text-primary);
    background: rgba(99, 102, 241, 0.08);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.25;
    cursor: not-allowed;
  }

  &.active {
    border-color: var(--color-primary);
    background: rgba(99, 102, 241, 0.12);
    color: var(--color-primary);
    box-shadow: 0 0 12px rgba(99, 102, 241, 0.15);
  }
`;

const TypeButtonStyled = styled(IconButtonStyled)`
  width: auto;
  grid-auto-flow: column;
  gap: 8px;
  padding: 0 14px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 20px;
  
  &.active {
    border-color: ${props => props.themeColor || "var(--color-primary)"};
    background: ${props => props.themeBg || "rgba(99, 102, 241, 0.1)"};
    color: var(--text-primary);
    box-shadow: none;
  }
  
  &:hover:not(:disabled):not(.active) {
    border-color: rgba(255, 255, 255, 0.15);
    background: rgba(255, 255, 255, 0.04);
  }
`;

const GroupsStyled = styled.div`
  display: grid;
  gap: 16px;
`;

const GroupStyled = styled.section`
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(20, 20, 25, 0.6) 0%, rgba(10, 10, 12, 0.8) 100%);
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.3);
  overflow: hidden;
  backdrop-filter: blur(10px);
`;

const GroupHeaderStyled = styled.header`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(255, 255, 255, 0.01);

  .group-title {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: var(--text-primary);
    letter-spacing: 0.25px;
  }

  .count {
    color: var(--text-muted);
    font-size: 12px;
    font-weight: 500;
    background: rgba(255, 255, 255, 0.04);
    padding: 2px 8px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.04);
  }

  .group-actions {
    display: flex;
    gap: 8px;
  }
`;

const MonitorListStyled = styled.div`
  display: grid;
  gap: 10px;
  padding: 14px;

  &.cards {
    grid-template-columns: repeat(4, minmax(240px, 1fr));

    @media (max-width: 1200px) {
      grid-template-columns: repeat(3, minmax(240px, 1fr));
    }

    @media (max-width: 720px) {
      grid-template-columns: 1fr;
    }
  }
`;

const MonitorRowStyled = styled(Link)`
  display: grid;
  grid-template-columns: 140px minmax(170px, 1.3fr) minmax(220px, 1.7fr) 100px 110px 90px;
  gap: 14px;
  align-items: center;
  padding: 14px 18px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-left: 3px solid ${ props => props.accent };
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(24, 24, 27, 0.4) 0%, rgba(15, 15, 18, 0.7) 100%);
  color: inherit;
  text-decoration: none;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    border-color: rgba(255, 255, 255, 0.12);
    background: linear-gradient(135deg, rgba(30, 30, 35, 0.5) 0%, rgba(20, 20, 25, 0.8) 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
    color: inherit;
    text-decoration: none;
  }

  @media (max-width: 1120px) {
    grid-template-columns: 120px minmax(170px, 1fr) 1fr 80px;

    .hide-medium {
      display: none;
    }
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    gap: 10px;
    padding: 16px;
  }
`;

const MonitorCardStyled = styled(Link)`
  min-height: 228px;
  display: grid;
  align-content: start;
  gap: 14px;
  padding: 18px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-top: 3px solid ${ props => props.accent };
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(24, 24, 27, 0.4) 0%, rgba(15, 15, 18, 0.7) 100%);
  color: inherit;
  text-decoration: none;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    border-color: rgba(255, 255, 255, 0.12);
    background: linear-gradient(135deg, rgba(30, 30, 35, 0.5) 0%, rgba(20, 20, 25, 0.8) 100%);
    transform: translateY(-3px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
    color: inherit;
    text-decoration: none;
  }
`;

const MonitorNameStyled = styled.div`
  min-width: 0;

  .name {
    display: block;
    overflow: hidden;
    color: var(--text-primary);
    font-weight: 700;
    font-size: 14.5px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sub {
    margin-top: 4px;
    color: var(--text-muted);
    font-size: 12px;
    font-weight: 500;
  }
`;

const StatusPillStyled = styled.span`
  width: fit-content;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.25px;
  line-height: 1.2;
  border: 1px solid rgba(255, 255, 255, 0.05);
  
  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    display: inline-block;
  }

  &.healthy {
    background: rgba(16, 185, 129, 0.08);
    color: var(--color-success);
    border-color: rgba(16, 185, 129, 0.15);
    
    &::before {
      background: var(--color-success);
      box-shadow: 0 0 8px var(--color-success);
    }
  }

  &.waiting {
    background: rgba(245, 158, 11, 0.08);
    color: var(--color-warning);
    border-color: rgba(245, 158, 11, 0.15);
    
    &::before {
      background: var(--color-warning);
      box-shadow: 0 0 8px var(--color-warning);
    }
  }

  &.stale {
    background: rgba(244, 63, 94, 0.08);
    color: var(--color-danger);
    border-color: rgba(244, 63, 94, 0.15);
    
    &::before {
      background: var(--color-danger);
      box-shadow: 0 0 8px var(--color-danger);
    }
  }

  &.paused {
    background: rgba(255, 255, 255, 0.03);
    color: var(--text-muted);
    border-color: rgba(255, 255, 255, 0.05);
    
    &::before {
      background: var(--text-muted);
    }
  }
`;

const PreviewStyled = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;

  .metric-chip {
    padding: 3px 8px;
    border: 1px solid rgba(255, 255, 255, 0.04);
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.015);
    color: var(--text-secondary);
    font-size: 11.5px;
    font-family: monospace;
    
    span {
      color: var(--text-muted);
      margin-right: 4px;
      font-weight: 500;
    }
  }
`;

const TagsWrapperStyled = styled.div`
  min-height: 20px;

  .tags-item-zll {
    font-size: 10px;
    padding: 1px 4px;
    height: 20px;
  }
`;

const AlarmCountStyled = styled.span`
  color: ${ props => props.active ? "var(--color-danger)" : "var(--text-muted)" };
  font-weight: 700;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const EmptyStateStyled = styled.div`
  display: grid;
  gap: 8px;
  justify-items: center;
  padding: 34px 18px;
  color: var(--text-secondary);
  text-align: center;

  h3 {
    margin: 0;
    color: var(--text-primary);
    font-size: 17px;
  }

  p {
    max-width: 420px;
    margin: 0;
    line-height: 1.45;
  }
`;

const AddMonitorStyled = styled.div`
  display: grid;
  align-items: center;
  gap: 12px;
  min-height: 76px;
  padding: 16px;
  border: 1.5px dashed rgba(99, 102, 241, 0.25);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.005);
  transition: all 0.25s ease;

  &.card {
    min-height: 228px;
    justify-items: center;
    text-align: center;
    align-content: center;
  }

  .add-title {
    color: var(--text-primary);
    font-weight: 600;
    font-size: 13.5px;
  }

  .add-copy {
    color: var(--text-muted);
    font-size: 12px;
    margin-top: 4px;
  }
  
  &:hover {
    border-color: var(--color-primary);
    background: rgba(99, 102, 241, 0.015);
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(99, 102, 241, 0.05);
  }
`;

const CardMetricAreaStyled = styled.div`
  .llddeeassdd {
    margin-top: 4px;
  }
`;

const ServersList = () => {
    return useObserver(() => (
        <ServerListObserved
            servers={ Object.values(serverStore.serversListObj) }
            uptime={ Object.values(uptimeStore.uptimeListObj) }
            mongo={ Object.values(mongoDbStore.mongoDbListObj) }
            canAddNewServers={ serverStore.canAddNewServers }
            canAddNewUptime={ uptimeStore.canAddNewUptime }
            canAddNewMongo={ mongoDbStore.canAddNewMongoDb }
            user={ userStore.user }
        />
    ));
};

export default ServersList;

const ServerListObserved = ({
    servers,
    uptime,
    mongo,
    canAddNewServers,
    canAddNewUptime,
    canAddNewMongo,
    user
}) => {
    const tableViewStorage = localStorage.getItem("tableViewStorage");
    const monitorFilter = localStorage.getItem("monitorFilter") || serverFilterTypes.createdAt.val;
    const monitorFilterAsc = localStorage.getItem("monitorFilterAsc");

    const [viewSort, setViewSort] = useState(getServerSeder());
    const [selectedTagId, setSelectedTagId] = useState(null);
    const [isTableView, setIsTableView] = useState(!!tableViewStorage);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedFilter, setSelectedFilter] = useState(monitorFilter);
    const [isAscFilter, setIsAscFilter] = useState(monitorFilterAsc === "true");
    const [visibleTypes, setVisibleTypes] = useState(() => {
        const stored = localStorage.getItem("visibleTypes");
        if (stored) {
            try {
                return JSON.parse(stored);
            } catch (e) {}
        }
        return {
            server: true,
            uptime: true,
            mongo: true
        };
    });

    const groups = useMemo(() => ({
        server: createGroup("server", servers),
        uptime: createGroup("uptime", uptime),
        mongo: createGroup("mongo", mongo)
    }), [servers, uptime, mongo]);

    const summary = useMemo(() => getSummary(groups, user, {
        server: canAddNewServers,
        uptime: canAddNewUptime,
        mongo: canAddNewMongo
    }), [groups, user, canAddNewServers, canAddNewUptime, canAddNewMongo]);

    const totalPosition = viewSort.length;
    const hasAnyMonitor = summary.totalMonitors > 0;
    const canAddAnyMonitor = canAddNewServers || canAddNewUptime || canAddNewMongo;

    const changeOrder = (position, type) => {
        const newIndex = type === "down" ? position + 1 : position - 1;

        if (newIndex < 0 || newIndex >= viewSort.length) {
            return;
        }

        const newSeder = [...viewSort];
        const currentItem = newSeder[position];
        newSeder[position] = newSeder[newIndex];
        newSeder[newIndex] = currentItem;
        localStorage.setItem("serverSeder", JSON.stringify(newSeder));
        setViewSort(newSeder);
    };

    const onTagChange = id => {
        if (id === selectedTagId) {
            setSelectedTagId(null);
            return;
        }

        setSelectedTagId(id);
    };

    const onViewChange = () => {
        const nextValue = !isTableView;
        setIsTableView(nextValue);

        if (nextValue) {
            localStorage.setItem("tableViewStorage", "hh");
            return;
        }

        localStorage.removeItem("tableViewStorage");
    };

    const onFilterChange = value => {
        setSelectedFilter(value);
        localStorage.setItem("monitorFilter", value);
        localStorage.setItem("monitorFilterUptime", value);
    };

    const onAscChange = () => {
        const nextValue = !isAscFilter;
        setIsAscFilter(nextValue);
        localStorage.setItem("monitorFilterAsc", nextValue.toString());
        localStorage.setItem("monitorFilterUptimeAsc", nextValue.toString());
    };

    const toggleType = type => {
        const nextVisibleTypes = {
            ...visibleTypes,
            [type]: !visibleTypes[type]
        };
        setVisibleTypes(nextVisibleTypes);
        localStorage.setItem("visibleTypes", JSON.stringify(nextVisibleTypes));
    };

    return (
        <PageStyled>
            <PageHeaderStyled>
                <div>
                    <h1>Servers</h1>
                    <p>
                        Monitor server agents, uptime checks, and MongoDB collectors from one compact operations view.
                    </p>
                </div>
                <div className="header-actions">
                    <AddMonitorButton canAdd={ canAddAnyMonitor }/>
                </div>
            </PageHeaderStyled>

            <SummaryGridStyled>
                { summary.cards.map(card => (
                    <SummaryCard key={ card.title } card={ card }/>
                )) }
            </SummaryGridStyled>

            <ToolbarStyled>
                <EuiFieldSearch
                    compressed={ true }
                    placeholder="Search monitors"
                    value={ searchTerm }
                    onChange={ event => setSearchTerm(event.target.value) }
                    aria-label="Search monitors"
                />

                <div className="tag-filter">
                    <span className="toolbar-label">Tags</span>
                    <TagsList
                        showMessage={ true }
                        serverTags={ selectedTagId ? [selectedTagId] : [] }
                        selectEvent={ onTagChange }
                    />
                </div>

                <SortControlStyled>
                    <EuiSuperSelect
                        compressed={ true }
                        valueOfSelected={ selectedFilter }
                        onChange={ onFilterChange }
                        options={ SORT_OPTIONS.map(filter => ({
                            value: filter.val,
                            inputDisplay: (
                                <SortOption item={ filter }/>
                            )
                        })) }
                    />
                    <EuiToolTip content={ isAscFilter ? "Sort ascending" : "Sort descending" }>
                        <IconButtonStyled type="button" onClick={ onAscChange } aria-label="Toggle sort direction">
                            <EuiIcon type={ isAscFilter ? "arrowDown" : "arrowUp" } size="s"/>
                        </IconButtonStyled>
                    </EuiToolTip>
                </SortControlStyled>

                <div className="type-filter" aria-label="Monitor type filters">
                    { DEFAULT_ORDER.map(type => (
                        <TypeButtonStyled
                            key={ type }
                            type="button"
                            className={ visibleTypes[type] ? "active" : "" }
                            onClick={ () => toggleType(type) }
                            aria-pressed={ visibleTypes[type] }
                            themeColor={ GROUPS[type].accent }
                            themeBg={ `${GROUPS[type].accent}15` }
                        >
                            <EuiIcon type={ GROUPS[type].icon } size="s"/>
                            { GROUPS[type].title }
                        </TypeButtonStyled>
                    )) }
                </div>

                <div className="view-toggle">
                    <EuiToolTip content={ isTableView ? "Switch to card view" : "Switch to dense rows" }>
                        <IconButtonStyled
                            type="button"
                            className="active"
                            onClick={ onViewChange }
                            aria-label="Toggle monitor view"
                        >
                            <EuiIcon type={ isTableView ? "grid" : "tableDensityExpanded" } size="m"/>
                        </IconButtonStyled>
                    </EuiToolTip>
                </div>
            </ToolbarStyled>

            { !hasAnyMonitor && (
                <EmptyState
                    title="No monitors yet"
                    copy="Create your first monitor to start collecting server, uptime, or MongoDB signals."
                    canAdd={ canAddAnyMonitor }
                />
            ) }

            { hasAnyMonitor && (
                <GroupsStyled>
                    { viewSort.map((type, index) => {
                        if (!visibleTypes[type]) {
                            return null;
                        }

                        const group = groups[type];
                        const visibleItems = getVisibleItems(
                            group.items,
                            selectedTagId,
                            searchTerm,
                            selectedFilter,
                            isAscFilter
                        );

                        return (
                            <MonitorGroup
                                key={ type }
                                type={ type }
                                group={ group }
                                items={ visibleItems }
                                position={ index }
                                totalPosition={ totalPosition }
                                isTableView={ isTableView }
                                selectedTagId={ selectedTagId }
                                searchTerm={ searchTerm }
                                canAdd={ getCanAddByType(type, canAddNewServers, canAddNewUptime, canAddNewMongo) }
                                onChangeOrder={ changeOrder }
                            />
                        );
                    }) }
                </GroupsStyled>
            ) }
        </PageStyled>
    );
};

const SummaryCard = ({ card }) => {
    let glowClass = "";
    if (card.title === "Active alarms" && card.value > 0) {
        glowClass = "glow-danger";
    } else if (card.title === "Waiting or stale" && card.value > 0) {
        glowClass = "glow-warning";
    } else if (card.title === "Total monitors") {
        glowClass = "glow-primary";
    } else if (card.title === "Plan usage") {
        glowClass = "glow-info";
    }

    return (
        <SummaryCardStyled className={ glowClass }>
            <div className="summary-top">
                <span>{ card.title }</span>
                <EuiIcon type={ card.icon } size="m" className="card-icon"/>
            </div>
            <div className="summary-value">{ card.value }</div>
            <div className="summary-caption">{ card.caption }</div>
        </SummaryCardStyled>
    );
};

const MonitorGroup = ({
    type,
    group,
    items,
    position,
    totalPosition,
    isTableView,
    selectedTagId,
    searchTerm,
    canAdd,
    onChangeOrder
}) => {
    const config = GROUPS[type];
    const showFilteredEmpty = group.items.length > 0 && items.length === 0;
    const listClassName = isTableView ? "rows" : "cards";

    return (
        <GroupStyled>
            <GroupHeaderStyled>
                <div className="group-title">
                    <EuiIcon type={ config.icon } size="m" color={ config.accent }/>
                    <h2>{ config.title }</h2>
                    <span className="count">{ items.length } of { group.items.length }</span>
                </div>
                <div className="group-actions">
                    <OrderButton
                        disabled={ position === 0 }
                        direction="up"
                        onClick={ () => onChangeOrder(position, "up") }
                    />
                    <OrderButton
                        disabled={ position === totalPosition - 1 }
                        direction="down"
                        onClick={ () => onChangeOrder(position, "down") }
                    />
                </div>
            </GroupHeaderStyled>

            { group.items.length === 0 && (
                <EmptyState
                    title={ `No ${ config.title } monitors` }
                    copy={ `Create a ${ config.singular } when you are ready to collect this signal.` }
                    canAdd={ canAdd }
                    compact={ true }
                />
            ) }

            { showFilteredEmpty && (
                <EmptyState
                    title="No matching monitors"
                    copy={ getFilteredEmptyCopy(selectedTagId, searchTerm) }
                    canAdd={ false }
                    compact={ true }
                />
            ) }

            { items.length > 0 && (
                <MonitorListStyled className={ listClassName }>
                    { items.map(item => {
                        if (isTableView) {
                            return (
                                <MonitorRow
                                    key={ item._id }
                                    type={ type }
                                    item={ item }
                                />
                            );
                        }

                        return (
                            <MonitorCard
                                key={ item._id }
                                type={ type }
                                item={ item }
                            />
                        );
                    }) }
                    <AddMonitorCard type={ type } canAdd={ canAdd } isTableView={ isTableView }/>
                </MonitorListStyled>
            ) }
        </GroupStyled>
    );
};

const MonitorRow = ({ type, item }) => {
    const config = GROUPS[type];
    const route = `${ config.route }/${ item._id }`;
    const status = getStatus(type, item);
    const alarmsCount = getActiveAlarms(item);
    const lastUpdate = getLastUpdate(type, item);
    const preview = getMetricPreview(type, item);

    return (
        <MonitorRowStyled to={ route } accent={ config.accent }>
            <StatusPillStyled className={ status.className }>
                { status.label }
            </StatusPillStyled>
            <MonitorName item={ item } type={ type }/>
            <Preview metrics={ preview }/>
            <AlarmCount alarmsCount={ alarmsCount }/>
            <div className="hide-medium">{ lastUpdate }</div>
            <TagsWrapperStyled className="hide-medium">
                <TagsMIniColors serverTags={ item.tags }/>
            </TagsWrapperStyled>
        </MonitorRowStyled>
    );
};

const MonitorCard = ({ type, item }) => {
    const config = GROUPS[type];
    const route = `${ config.route }/${ item._id }`;
    const status = getStatus(type, item);
    const alarmsCount = getActiveAlarms(item);
    const preview = getMetricPreview(type, item);

    return (
        <MonitorCardStyled to={ route } accent={ config.accent }>
            <StatusPillStyled className={ status.className }>
                { status.label }
            </StatusPillStyled>
            <MonitorName item={ item } type={ type }/>
            <Preview metrics={ preview }/>
            <AlarmCount alarmsCount={ alarmsCount }/>
            <TagsWrapperStyled>
                <TagsMIniColors serverTags={ item.tags }/>
            </TagsWrapperStyled>
            <CardMetrics type={ type } item={ item }/>
        </MonitorCardStyled>
    );
};

const MonitorName = ({ item, type }) => {
    const sub = getMonitorSubText(type, item);

    return (
        <MonitorNameStyled>
            <span className="name">{ item.name || "Unnamed monitor" }</span>
            <span className="sub">{ sub }</span>
        </MonitorNameStyled>
    );
};

const Preview = ({ metrics }) => (
    <PreviewStyled>
        { metrics.map(metric => (
            <span key={ `${ metric.label }-${ metric.value }` } className="metric-chip">
                <span>{ metric.label }:</span>{ metric.value }
            </span>
        )) }
    </PreviewStyled>
);

const AlarmCount = ({ alarmsCount }) => (
    <AlarmCountStyled active={ alarmsCount > 0 }>
        { alarmsCount } alarms
    </AlarmCountStyled>
);

const CardMetrics = ({ type, item }) => {
    if (type === "server") {
        const metric = getServerMetric(item);
        const data = item.serverData || {};

        if (!metric || !data.agentPushedTheData) {
            return null;
        }

        return (
            <CardMetricAreaStyled>
                <FillBar name="CPU" total={ 100 } current={ metric.load_cpu } smaller={ true }/>
                { data.ramDataTotal && data.ramDataCurrent && (
                    <FillBar
                        name="RAM"
                        total={ data.ramDataTotal.value }
                        totalUnit={ data.ramDataTotal.unit }
                        current={ data.ramDataCurrent.value }
                        currentUnit={ data.ramDataCurrent.unit }
                        originalTotal={ metric.ram_total }
                        originalCurrent={ metric.ram_usage }
                        smaller={ true }
                    />
                ) }
            </CardMetricAreaStyled>
        );
    }

    if (type === "mongo" && item.agentPushedTheData && item.totalSizeFormated) {
        return (
            <CardMetricAreaStyled>
                <FillBar
                    name="Storage"
                    total={ item.totalSizeFormated.value }
                    totalUnit={ item.totalSizeFormated.unit }
                    current={ item.totalDbUsageFormated.value }
                    currentUnit={ item.totalDbUsageFormated.unit }
                    originalTotal={ item.metric.totalSize }
                    originalCurrent={ item.totalDbUsage }
                    smaller={ true }
                />
            </CardMetricAreaStyled>
        );
    }

    return null;
};

const AddMonitorCard = ({ type, canAdd, isTableView }) => {
    const config = GROUPS[type];
    const className = isTableView ? "" : "card";

    return (
        <AddMonitorStyled className={ className }>
            <EuiIcon type="plusInCircle" size="m" color={ canAdd ? config.accent : "subdued" }/>
            <div>
                <div className="add-title">Add { config.singular }</div>
                <div className="add-copy">
                    { canAdd ? "Create a monitor from the setup flow." : "Plan limit reached for this monitor type." }
                </div>
            </div>
            <AddMonitorButton canAdd={ canAdd } size="s"/>
        </AddMonitorStyled>
    );
};

const AddMonitorButton = ({ canAdd, size = "s" }) => {
    if (!canAdd) {
        return (
            <EuiButton size={ size } iconType="plusInCircle" isDisabled={ true }>
                Limit reached
            </EuiButton>
        );
    }

    return (
        <Link to="/servers-new">
            <EuiButton size={ size } fill iconType="plusInCircle">
                Add monitor
            </EuiButton>
        </Link>
    );
};

const OrderButton = ({ direction, disabled, onClick }) => {
    const icon = direction === "up" ? "arrowUp" : "arrowDown";
    const label = direction === "up" ? "Move group up" : "Move group down";

    return (
        <EuiToolTip content={ label }>
            <IconButtonStyled
                type="button"
                disabled={ disabled }
                onClick={ onClick }
                aria-label={ label }
            >
                <EuiIcon type={ icon } size="s"/>
            </IconButtonStyled>
        </EuiToolTip>
    );
};

const SortOption = ({ item }) => (
    <div>
        <EuiIcon type={ item.iconType } size="s"/>
        <span style={ { marginLeft: 8 } }>{ item.label }</span>
    </div>
);

const EmptyState = ({ title, copy, canAdd, compact = false }) => (
    <EmptyStateStyled style={ compact ? { padding: "24px 18px" } : null }>
        <EuiIcon type="visGauge" size="l" color="subdued"/>
        <h3>{ title }</h3>
        <p>{ copy }</p>
        { canAdd && (
            <AddMonitorButton canAdd={ true }/>
        ) }
    </EmptyStateStyled>
);

const getServerSeder = () => {
    const seder = localStorage.getItem("serverSeder");

    if (!seder) {
        return DEFAULT_ORDER;
    }

    try {
        const parsedSeder = JSON.parse(seder);
        const validSeder = parsedSeder.filter(item => DEFAULT_ORDER.includes(item));
        const missingItems = DEFAULT_ORDER.filter(item => !validSeder.includes(item));

        return [...validSeder, ...missingItems];
    } catch (e) {
        return DEFAULT_ORDER;
    }
};

const createGroup = (type, items) => ({
    type,
    items: items || []
});

const getSummary = (groups, user, canAdd) => {
    const totalServers = groups.server.items.length;
    const totalUptime = groups.uptime.items.length;
    const totalMongo = groups.mongo.items.length;
    const totalMonitors = totalServers + totalUptime + totalMongo;
    const activeAlarms = DEFAULT_ORDER.reduce((total, type) => {
        return total + groups[type].items.reduce((count, item) => count + getActiveAlarms(item), 0);
    }, 0);
    const staleMonitors = DEFAULT_ORDER.reduce((total, type) => {
        return total + groups[type].items.filter(item => {
            const status = getStatus(type, item);
            return status.className === "stale" || status.className === "waiting";
        }).length;
    }, 0);
    const limits = getSubscriptionLimits(user);
    const limitsCopy = getLimitsCopy({
        totalServers,
        totalUptime,
        totalMongo,
        limits,
        canAdd
    });

    return {
        totalMonitors,
        cards: [
            {
                title: "Total monitors",
                value: totalMonitors,
                caption: `${ totalServers } servers, ${ totalUptime } uptime, ${ totalMongo } MongoDB`,
                icon: "monitoringApp"
            },
            {
                title: "Active alarms",
                value: activeAlarms,
                caption: activeAlarms > 0 ? "Needs attention" : "No active alarms",
                icon: "alert"
            },
            {
                title: "Waiting or stale",
                value: staleMonitors,
                caption: "No payload or delayed signal",
                icon: "timeRefresh"
            },
            {
                title: "Plan usage",
                value: limitsCopy.value,
                caption: limitsCopy.caption,
                icon: "package"
            }
        ]
    };
};

const getSubscriptionLimits = user => {
    if (!user || !user.subscription) {
        return {
            server: 0,
            uptime: 0,
            mongo: 0
        };
    }

    return {
        server: user.subscription.servers || 0,
        uptime: user.subscription.uptime || 0,
        mongo: user.subscription.mongo || 0
    };
};

const getLimitsCopy = ({ totalServers, totalUptime, totalMongo, limits, canAdd }) => {
    const used = totalServers + totalUptime + totalMongo;
    const total = limits.server + limits.uptime + limits.mongo;
    const canAddAny = canAdd.server || canAdd.uptime || canAdd.mongo;

    if (total > 0) {
        return {
            value: `${ used }/${ total }`,
            caption: canAddAny ? "Monitor slots available" : "All monitor limits reached"
        };
    }

    return {
        value: canAddAny ? "Open" : "Full",
        caption: canAddAny ? "Monitor slots available" : "All monitor limits reached"
    };
};

const getVisibleItems = (items, selectedTagId, searchTerm, selectedFilter, isAscFilter) => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    let nextItems = [...items];

    if (selectedTagId) {
        nextItems = nextItems.filter(item => {
            if (!item.tags || !item.tags.length) {
                return false;
            }

            return item.tags.includes(selectedTagId);
        });
    }

    if (normalizedSearch) {
        nextItems = nextItems.filter(item => doesItemMatchSearch(item, normalizedSearch));
    }

    return sortItems(nextItems, selectedFilter, isAscFilter);
};

const doesItemMatchSearch = (item, normalizedSearch) => {
    const searchableValues = [
        item.name,
        item.type,
        item._id
    ];
    const metric = getServerMetric(item);

    if (metric && metric.ipv4) {
        searchableValues.push(metric.ipv4);
    }

    return searchableValues.some(value => {
        if (!value) {
            return false;
        }

        return value.toString().toLowerCase().includes(normalizedSearch);
    });
};

const sortItems = (items, selectedFilter, isAscFilter) => {
    return [...items].sort((a, b) => {
        const valueA = getSortValue(a, selectedFilter, isAscFilter);
        const valueB = getSortValue(b, selectedFilter, isAscFilter);

        if (typeof valueA === "string" || typeof valueB === "string") {
            if (isAscFilter) {
                return valueA.toString().localeCompare(valueB.toString());
            }

            return valueB.toString().localeCompare(valueA.toString());
        }

        if (isAscFilter) {
            return valueA - valueB;
        }

        return valueB - valueA;
    });
};

const getSortValue = (item, selectedFilter, isAscFilter) => {
    const defaultString = isAscFilter ? "z" : "";
    const defaultNumber = isAscFilter ? Number.MAX_SAFE_INTEGER : 0;

    if (selectedFilter === serverFilterTypes.name.val) {
        return item.name || defaultString;
    }

    if (selectedFilter === serverFilterTypes.alarms.val) {
        return getActiveAlarms(item);
    }

    if (selectedFilter === serverFilterTypes.tags.val) {
        if (item.tags && item.tags.length) {
            return item.tags[0];
        }

        return defaultString;
    }

    return item.createdAt || defaultNumber;
};

const getCanAddByType = (type, canAddNewServers, canAddNewUptime, canAddNewMongo) => {
    if (type === "server") {
        return canAddNewServers;
    }

    if (type === "uptime") {
        return canAddNewUptime;
    }

    return canAddNewMongo;
};

const getStatus = (type, item) => {
    let className = item.classes;

    if (type === "server" && item.serverData) {
        className = item.serverData.classes;
    }

    if (statusMeta[className]) {
        return statusMeta[className];
    }

    return {
        label: "Unknown",
        className: "waiting"
    };
};

const getActiveAlarms = item => {
    if (!item.alarms || !item.alarms.length) {
        return 0;
    }

    return item.alarms.filter(alarm => alarm.status === 1).length;
};

const getLastUpdate = (type, item) => {
    if (type === "server" && item.serverData) {
        return item.serverData.timeAgo || "N/A";
    }

    return item.timeAgo || "N/A";
};

const getServerMetric = item => {
    if (!item.metrics || !item.metrics.length) {
        return null;
    }

    return item.metrics[0];
};

const getMetricPreview = (type, item) => {
    if (type === "server") {
        return getServerPreview(item);
    }

    if (type === "uptime") {
        return getUptimePreview(item);
    }

    return getMongoPreview(item);
};

const getServerPreview = item => {
    const metric = getServerMetric(item);
    const data = item.serverData || {};

    if (!metric || !data.agentPushedTheData) {
        return [
            {
                label: "Payload",
                value: "waiting"
            }
        ];
    }

    return [
        {
            label: "CPU",
            value: `${ metric.load_cpu || 0 }%`
        },
        {
            label: "Load",
            value: metric.load || "N/A"
        },
        {
            label: "IP",
            value: metric.ipv4 || "N/A"
        }
    ];
};

const getUptimePreview = item => {
    const interval = utilsService.calculateEveryTime(item);
    const values = [
        {
            label: "Status",
            value: item.isEnabled ? "enabled" : "paused"
        },
        {
            label: "Every",
            value: interval
        }
    ];

    if (item.isEnabled && item.scheduleTo) {
        values.push({
            label: "Next",
            value: dayjs(item.scheduleTo).format("HH:mm DD MMM")
        });
    }

    return values;
};

const getMongoPreview = item => {
    if (!item.agentPushedTheData) {
        return [
            {
                label: "Payload",
                value: "waiting"
            }
        ];
    }

    return [
        {
            label: "Hosts",
            value: item.numberOfHosts || "N/A"
        },
        {
            label: "Storage",
            value: getMongoStorage(item)
        },
        {
            label: "Connections",
            value: item.connections || 0
        }
    ];
};

const getMongoStorage = item => {
    if (!item.totalDbUsageFormated || !item.totalSizeFormated) {
        return "N/A";
    }

    return `${ item.totalDbUsageFormated.value }${ item.totalDbUsageFormated.unit }/${ item.totalSizeFormated.value }${ item.totalSizeFormated.unit }`;
};

const getMonitorSubText = (type, item) => {
    if (type === "server") {
        return "Linux agent";
    }

    if (type === "uptime") {
        return item.type || "Endpoint check";
    }

    return `${ item.numberOfHosts || 0 } hosts`;
};

const getFilteredEmptyCopy = (selectedTagId, searchTerm) => {
    if (selectedTagId && searchTerm) {
        return "No monitors match the selected tag and search text.";
    }

    if (selectedTagId) {
        return "No monitors match the selected tag.";
    }

    return "No monitors match the current search text.";
};

export const ServersListStyled = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(150px, 1fr));
  gap: 10px;
  padding: 5px;

  &.table {
    grid-template-columns: 1fr;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const FilterGroupStyled = styled.div`
  display: grid;
  gap: 10px;
  align-items: center;
`;

export const TagsListWrapperStyled = styled.div`
  display: grid;
  grid-template-columns: auto auto 1fr;
  gap: 8px;
  align-items: center;

  .position-kdz svg {
    cursor: pointer;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const ServersItemStyled = styled.div`
  min-height: 100px;
  padding: 10px;
  position: relative;
  border-radius: 7px;
  background: rgb(29, 30, 36);
`;

export const NewServerItem = () => (
    <AddMonitorStyled className="card">
        <EuiIcon type="plusInCircle" size="m" color="#66d9ef"/>
        <div>
            <div className="add-title">Add monitor</div>
            <div className="add-copy">Create a monitor from the setup flow.</div>
        </div>
        <AddMonitorButton canAdd={ true } size="s"/>
    </AddMonitorStyled>
);

export const FilterItemDisplay = ({ item }) => (
    <SortOption item={ item }/>
);

export const AscDescFilter = ({ isAck, onClick }) => (
    <IconButtonStyled type="button" onClick={ onClick } aria-label="Toggle sort direction">
        <EuiIcon type={ isAck ? "arrowDown" : "arrowUp" } size="s"/>
    </IconButtonStyled>
);
